
import React, {
    useCallback,
    useEffect,
    useMemo,
    useState
} from "react";

import Select from "react-select";
import Swal from "sweetalert2";

import CashVoucherServices from "../../../services/CashVoucherServices";
import { downloadCashVoucherPDF } from "./CashVoucherPDF";

// Adjust the service import path if your
// components directory is structured differently.

const money = (value) =>
    `₹${Number(value || 0).toLocaleString("en-IN", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    })}`;

const getId = (value) =>
    String(value?._id || value || "");

const getName = (value, fallback = "-") => {
    if (!value) return fallback;

    if (typeof value === "string") return value;

    return (
        value.name ||
        value.location ||
        value.description ||
        fallback
    );
};

const getError = (error) =>
    error?.response?.data?.message ||
    error?.message ||
    "Something went wrong. Please try again.";

const unwrap = (response) =>
    response?.data?.data ||
    response?.data ||
    response ||
    {};

const getArray = (response, keys) => {
    const data = unwrap(response);

    if (Array.isArray(data)) return data;

    for (const key of keys) {
        if (Array.isArray(data?.[key])) {
            return data[key];
        }
    }

    return [];
};

const todayIST = () => {
    const parts = new Intl.DateTimeFormat("en-GB", {
        timeZone: "Asia/Kolkata",
        day: "2-digit",
        month: "2-digit",
        year: "numeric"
    }).formatToParts(new Date());

    const values = Object.fromEntries(
        parts.map((part) => [part.type, part.value])
    );

    return `${values.year}-${values.month}-${values.day}`;
};

const CashVoucherGenerator = () => {
    const [stores, setStores] = useState([]);
    const [passbooks, setPassbooks] = useState([]);

    const [selectedStore, setSelectedStore] = useState(null);
    const [selectedPassbook, setSelectedPassbook] =
        useState(null);

    const [voucherDate, setVoucherDate] = useState(
        todayIST()
    );

    const [entries, setEntries] = useState([]);
    const [existingVouchers, setExistingVouchers] =
        useState([]);

    const [selectedEntryIds, setSelectedEntryIds] =
        useState([]);

    const [voucherMode, setVoucherMode] = useState("new");
    const [selectedVoucher, setSelectedVoucher] =
        useState(null);

    const [loadingStores, setLoadingStores] =
        useState(false);
    const [loadingPassbooks, setLoadingPassbooks] =
        useState(false);
    const [loadingEntries, setLoadingEntries] =
        useState(false);
    const [submitting, setSubmitting] = useState(false);

    const [loadedSelection, setLoadedSelection] =
        useState(null);

    const [lastVoucher, setLastVoucher] = useState(null);

    // ==========================================
    // CASH VOUCHER PDF STATE
    // ==========================================

    const [pdfDownloadingId, setPdfDownloadingId] =
        useState(null);

    // ==========================================
    // LOAD STORES
    // ==========================================

    useEffect(() => {
        let active = true;

        const loadStores = async () => {
            setLoadingStores(true);

            try {
                const response =
                    await CashVoucherServices.getStores();

                if (!active) return;

                setStores(
                    getArray(response, ["stores", "locations"])
                );
            } catch (error) {
                if (active) {
                    Swal.fire(
                        "Error",
                        getError(error),
                        "error"
                    );
                }
            } finally {
                if (active) setLoadingStores(false);
            }
        };

        loadStores();

        return () => {
            active = false;
        };
    }, []);

    // ==========================================
    // STORE OPTIONS
    // ==========================================

    const storeOptions = useMemo(
        () =>
            stores.map((store) => ({
                value: getId(store),
                label:
                    getName(store) +
                    (store.location
                        ? ` - ${store.location}`
                        : "")
            })),
        [stores]
    );

    const passbookOptions = useMemo(
        () =>
            passbooks.map((passbook) => ({
                value: getId(passbook),
                label: getName(passbook)
            })),
        [passbooks]
    );

    const voucherOptions = useMemo(
        () =>
            existingVouchers.map((voucher) => ({
                value: getId(voucher),
                label: `${voucher.voucherNumber} | ${money(
                    voucher.totalAmount
                )} | R${voucher.revision || 0}`
            })),
        [existingVouchers]
    );

    // ==========================================
    // RESET RESULTS
    // ==========================================

    const resetResults = () => {
        setEntries([]);
        setExistingVouchers([]);
        setSelectedEntryIds([]);
        setVoucherMode("new");
        setSelectedVoucher(null);
        setLoadedSelection(null);
        setLastVoucher(null);
    };

    // ==========================================
    // STORE CHANGE
    // ==========================================

    const handleStoreChange = async (option) => {
        setSelectedStore(option);
        setSelectedPassbook(null);
        setPassbooks([]);
        resetResults();

        if (!option) return;

        setLoadingPassbooks(true);

        try {
            const response =
                await CashVoucherServices.getPassbooks(
                    option.value
                );

            setPassbooks(
                getArray(response, ["passbooks", "cashPassbooks"])
            );
        } catch (error) {
            Swal.fire("Error", getError(error), "error");
        } finally {
            setLoadingPassbooks(false);
        }
    };

    // ==========================================
    // LOAD ELIGIBLE ENTRIES
    // ==========================================

    const loadEligibleEntries = useCallback(
        async (passbookId, date) => {
            setLoadingEntries(true);

            try {
                const response =
                    await CashVoucherServices.getEligibleEntries({
                        passbookId,
                        voucherDate: date
                    });

                const data = unwrap(response);

                setEntries(
                    Array.isArray(data.entries) ? data.entries : []
                );

                setExistingVouchers(
                    Array.isArray(data.existingVouchers)
                        ? data.existingVouchers
                        : []
                );

                setSelectedEntryIds([]);
                setVoucherMode("new");
                setSelectedVoucher(null);

                setLoadedSelection(`${passbookId}|${date}`);

                return data;
            } catch (error) {
                setLoadedSelection(null);

                Swal.fire(
                    "Unable to load entries",
                    getError(error),
                    "error"
                );

                return null;
            } finally {
                setLoadingEntries(false);
            }
        },
        []
    );

    const handleSearch = async () => {
        if (!selectedStore) {
            return Swal.fire(
                "Select Store",
                "Please select Store / Location.",
                "warning"
            );
        }

        if (!selectedPassbook) {
            return Swal.fire(
                "Select CashBook",
                "Please select a CashBook.",
                "warning"
            );
        }

        if (!voucherDate) {
            return Swal.fire(
                "Select Date",
                "Please select voucher date.",
                "warning"
            );
        }

        setLastVoucher(null);

        await loadEligibleEntries(
            selectedPassbook.value,
            voucherDate
        );
    };

    // ==========================================
    // ENTRY SELECTION
    // ==========================================

    const allIds = useMemo(
        () => entries.map((entry) => getId(entry)),
        [entries]
    );

    const allSelected =
        allIds.length > 0 &&
        allIds.every((id) => selectedEntryIds.includes(id));

    const toggleAll = () => {
        setSelectedEntryIds(
            allSelected ? [] : [...allIds]
        );
    };

    const toggleEntry = (id) => {
        setSelectedEntryIds((previous) =>
            previous.includes(id)
                ? previous.filter((item) => item !== id)
                : [...previous, id]
        );
    };

    const selectedEntries = useMemo(
        () =>
            entries.filter((entry) =>
                selectedEntryIds.includes(getId(entry))
            ),
        [entries, selectedEntryIds]
    );

    const selectedTotal = useMemo(
        () =>
            selectedEntries.reduce(
                (total, entry) => total + Number(entry.amount || 0),
                0
            ),
        [selectedEntries]
    );

    const availableTotal = useMemo(
        () =>
            entries.reduce(
                (total, entry) => total + Number(entry.amount || 0),
                0
            ),
        [entries]
    );

    // ==========================================
    // GENERATE / MERGE VOUCHER
    // ==========================================

    const handleSubmit = async () => {
        if (!selectedEntryIds.length) {
            return Swal.fire(
                "Select Entries",
                "Please select at least one Cash Out entry.",
                "warning"
            );
        }

        if (
            loadedSelection !==
            `${selectedPassbook?.value}|${voucherDate}`
        ) {
            return Swal.fire(
                "Refresh Required",
                "Please load the entries again.",
                "warning"
            );
        }

        if (voucherMode === "merge" && !selectedVoucher) {
            return Swal.fire(
                "Select Voucher",
                "Please select the existing voucher.",
                "warning"
            );
        }

        const isMerge = voucherMode === "merge";

        const actionText = isMerge
            ? `Merge ${selectedEntryIds.length} entries into ${selectedVoucher.label
            }?`
            : `Generate a new voucher for ${selectedEntryIds.length
            } entries?`;

        const confirmation = await Swal.fire({
            title: isMerge
                ? "Confirm Voucher Merge"
                : "Confirm Voucher Generation",
            html: `
        <div style="text-align:left">
          <p>${actionText}</p>
          <p><strong>Selected Amount:</strong>
          ${money(selectedTotal)}</p>
          ${isMerge
                    ? "<p>The existing voucher total and revision will be updated.</p>"
                    : ""
                }
        </div>
      `,
            icon: "question",
            showCancelButton: true,
            confirmButtonText: isMerge
                ? "Yes, Merge"
                : "Yes, Generate",
            cancelButtonText: "Cancel"
        });

        if (!confirmation.isConfirmed) return;

        setSubmitting(true);

        try {
            let response;

            if (isMerge) {
                response =
                    await CashVoucherServices.mergeVoucher(
                        selectedVoucher.value,
                        selectedEntryIds
                    );
            } else {
                response =
                    await CashVoucherServices.generateVoucher({
                        passbookId: selectedPassbook.value,
                        voucherDate,
                        entryIds: selectedEntryIds
                    });
            }

            const data = unwrap(response);
            const voucher = data.voucher;

            setLastVoucher(voucher || null);

            await Swal.fire({
                icon: "success",
                title: isMerge
                    ? "Voucher Updated"
                    : "Voucher Generated",
                text: voucher?.voucherNumber
                    ? `Voucher No: ${voucher.voucherNumber}`
                    : "Operation completed successfully."
            });

            await loadEligibleEntries(
                selectedPassbook.value,
                voucherDate
            );

            setLastVoucher(voucher || null);
        } catch (error) {
            Swal.fire(
                "Operation Failed",
                getError(error),
                "error"
            );
        } finally {
            setSubmitting(false);
        }
    };

    // ==========================================
    // DOWNLOAD CASH VOUCHER PDF
    // ==========================================

    const handleDownloadVoucher = async (voucherId) => {
        if (!voucherId || pdfDownloadingId) return;

        setPdfDownloadingId(voucherId);

        try {
            await downloadCashVoucherPDF(voucherId);
        } catch (error) {
            Swal.fire(
                "PDF Generation Failed",
                getError(error),
                "error"
            );
        } finally {
            setPdfDownloadingId(null);
        }
    };

    // ==========================================
    // VIEW VOUCHER DETAILS
    // ==========================================

    const handleViewVoucher = async (voucherId) => {
        try {
            const response =
                await CashVoucherServices.getVoucher(voucherId);

            const voucher = unwrap(response).voucher;

            if (!voucher) {
                throw new Error("Voucher details not found.");
            }

            const entryRows = (voucher.entries || [])
                .map(
                    (entry) => `
            <tr>
              <td>${entry.requestNumber || "-"}</td>
              <td>${getName(entry.category)}</td>
              <td style="text-align:right">
                ${money(entry.amount)}
              </td>
            </tr>
          `
                )
                .join("");

            await Swal.fire({
                title: voucher.voucherNumber,
                width: 750,
                html: `
          <div style="text-align:left">
            <p><strong>Date:</strong> ${voucher.voucherDate
                    }</p>
            <p><strong>Revision:</strong> R${voucher.revision || 0
                    }</p>
            <p><strong>CashBook:</strong> ${getName(voucher.passbook)
                    }</p>
            <div style="overflow-x:auto">
              <table class="table table-bordered table-sm">
                <thead>
                  <tr>
                    <th>Entry No.</th>
                    <th>Category</th>
                    <th>Amount</th>
                  </tr>
                </thead>
                <tbody>${entryRows}</tbody>
              </table>
            </div>
            <p><strong>Total:</strong>
              ${money(voucher.totalAmount)}
            </p>
          </div>
        `,
                confirmButtonText: "Close"
            });
        } catch (error) {
            Swal.fire("Error", getError(error), "error");
        }
    };

    const isBusy =
        loadingStores ||
        loadingPassbooks ||
        loadingEntries ||
        submitting;

    // ==========================================
    // RENDER
    // ==========================================

    return (
        <div className="container-fluid py-3">
            <div className="card shadow-sm border-0 mb-3">
                <div className="card-header bg-white">
                    <h5 className="mb-1">
                        <i className="fa fa-file-text-o me-2" />
                        Cash Voucher Generator
                    </h5>

                    <small className="text-muted">
                        Generate or merge approved Cash Out entries
                        CashBook-wise and date-wise.
                    </small>
                </div>

                <div className="card-body">

                    {/* STORE / CASHBOOK / DATE / GET ENTRIES */}
                    <div className="row g-3 align-items-end">

                        {/* STORE / LOCATION */}
                        <div className="col-12 col-md-4">
                            <label className="form-label">
                                Store / Location
                            </label>

                            <Select
                                options={storeOptions}
                                value={selectedStore}
                                onChange={handleStoreChange}
                                isClearable
                                isLoading={loadingStores}
                                isDisabled={isBusy}
                                placeholder="Select Store"
                                styles={{
                                    control: (base) => ({
                                        ...base,
                                        minHeight: "48px",
                                        height: "48px",
                                        borderRadius: "8px"
                                    }),
                                    valueContainer: (base) => ({
                                        ...base,
                                        height: "46px"
                                    }),
                                    indicatorsContainer: (base) => ({
                                        ...base,
                                        height: "46px"
                                    })
                                }}
                            />
                        </div>

                        {/* CASHBOOK / PASSBOOK */}
                        <div className="col-12 col-md-4">
                            <label className="form-label">
                                CashBook / Passbook
                            </label>

                            <Select
                                options={passbookOptions}
                                value={selectedPassbook}
                                onChange={(option) => {
                                    setSelectedPassbook(option);
                                    resetResults();
                                }}
                                isClearable
                                isLoading={loadingPassbooks}
                                isDisabled={!selectedStore || isBusy}
                                placeholder="Select CashBook"
                                styles={{
                                    control: (base) => ({
                                        ...base,
                                        minHeight: "48px",
                                        height: "48px",
                                        borderRadius: "8px"
                                    }),
                                    valueContainer: (base) => ({
                                        ...base,
                                        height: "46px"
                                    }),
                                    indicatorsContainer: (base) => ({
                                        ...base,
                                        height: "46px"
                                    })
                                }}
                            />
                        </div>

                        {/* VOUCHER DATE */}
                        <div className="col-12 col-md-2">
                            <label className="form-label">
                                Voucher For Date
                            </label>

                            <input
                                type="date"
                                className="form-control"
                                value={voucherDate}
                                max={todayIST()}
                                disabled={isBusy}
                                onChange={(event) => {
                                    setVoucherDate(event.target.value);
                                    resetResults();
                                }}
                                style={{
                                    height: "48px",
                                    minHeight: "48px",
                                    borderRadius: "8px",
                                    padding: "0 12px",
                                    width: "100%"
                                }}
                            />
                        </div>

                        {/* GET ENTRIES BUTTON */}
                        <div className="col-12 col-md-2">
                            <button
                                type="button"
                                className="btn btn-primary w-100 d-flex align-items-center justify-content-center"
                                disabled={isBusy}
                                onClick={handleSearch}
                                style={{
                                    height: "48px",
                                    minHeight: "48px",
                                    borderRadius: "8px",
                                    fontSize: "14px",
                                    fontWeight: 600,
                                    whiteSpace: "nowrap"
                                }}
                            >
                                {loadingEntries ? (
                                    <>
                                        <i className="fa fa-spinner fa-spin me-2" />
                                        Loading...
                                    </>
                                ) : (
                                    <>
                                        <i className="fa fa-search me-2" />
                                        Get Entries
                                    </>
                                )}
                            </button>
                        </div>

                    </div>

                </div>
            </div>

            {loadedSelection && (
                <>

                    <div className="row g-3 mb-3">

                        {/* ELIGIBLE ENTRIES - BLUE */}
                        <div className="col-md-4">
                            <div
                                className="card shadow-sm h-100"
                                style={{
                                    background: "linear-gradient(120deg, #e5f0ff 0%, #d3e5ff 55%, #c1dbff 100%)",
                                    border: "1px solid #b7d4fa",
                                    borderRadius: "16px"
                                }}
                            >
                                <div
                                    className="card-body"
                                    style={{ padding: "22px 26px" }}
                                >
                                    <small
                                        style={{
                                            color: "#42658e",
                                            fontSize: "14px",
                                            fontWeight: 600
                                        }}
                                    >
                                        Eligible Entries
                                    </small>

                                    <h4
                                        className="mb-0 mt-2"
                                        style={{
                                            color: "#164b91",
                                            fontSize: "28px",
                                            fontWeight: 700
                                        }}
                                    >
                                        {entries.length}
                                    </h4>
                                </div>
                            </div>
                        </div>

                        {/* AVAILABLE AMOUNT - GREEN */}
                        <div className="col-md-4">
                            <div
                                className="card shadow-sm h-100"
                                style={{
                                    background: "linear-gradient(120deg, #e5f8ee 0%, #d2f2e1 55%, #bcebd1 100%)",
                                    border: "1px solid #a9dfc3",
                                    borderRadius: "16px"
                                }}
                            >
                                <div
                                    className="card-body"
                                    style={{ padding: "22px 26px" }}
                                >
                                    <small
                                        style={{
                                            color: "#38775b",
                                            fontSize: "14px",
                                            fontWeight: 600
                                        }}
                                    >
                                        Available Amount
                                    </small>

                                    <h4
                                        className="mb-0 mt-2"
                                        style={{
                                            color: "#12663e",
                                            fontSize: "28px",
                                            fontWeight: 700
                                        }}
                                    >
                                        {money(availableTotal)}
                                    </h4>
                                </div>
                            </div>
                        </div>

                        {/* EXISTING VOUCHERS - PURPLE */}
                        <div className="col-md-4">
                            <div
                                className="card shadow-sm h-100"
                                style={{
                                    background: "linear-gradient(120deg, #f1eaff 0%, #e5d8ff 55%, #d8c6ff 100%)",
                                    border: "1px solid #d5bdf8",
                                    borderRadius: "16px"
                                }}
                            >
                                <div
                                    className="card-body"
                                    style={{ padding: "22px 26px" }}
                                >
                                    <small
                                        style={{
                                            color: "#73549b",
                                            fontSize: "14px",
                                            fontWeight: 600
                                        }}
                                    >
                                        Existing Vouchers
                                    </small>

                                    <h4
                                        className="mb-0 mt-2"
                                        style={{
                                            color: "#653c9d",
                                            fontSize: "28px",
                                            fontWeight: 700
                                        }}
                                    >
                                        {existingVouchers.length}
                                    </h4>
                                </div>
                            </div>
                        </div>

                    </div>



                    {/* EXISTING VOUCHERS */}
                    {existingVouchers.length > 0 && (
                        <div className="card border-0 shadow-sm mb-3">
                            <div className="card-header bg-white">
                                <strong>
                                    Existing Vouchers for Selected Date
                                </strong>
                            </div>

                            <div className="card-body">
                                <div className="table-responsive">
                                    <table className="table table-bordered table-sm align-middle">
                                        <thead className="table-light">
                                            <tr>
                                                <th>Voucher No.</th>
                                                <th>Entries</th>
                                                <th>
                                                    Total Amount
                                                </th>
                                                <th className="text-center">
                                                    Action
                                                </th>
                                            </tr>
                                        </thead>

                                        <tbody>
                                            {existingVouchers.map((voucher) => {
                                                const voucherId = getId(voucher);

                                                return (
                                                    <tr key={voucherId}>
                                                        <td>
                                                            {voucher.voucherNumber}
                                                        </td>
                                                        <td>
                                                            {voucher.totalEntries}
                                                        </td>

                                                        <td>
                                                            {money(voucher.totalAmount)}
                                                        </td>

                                                        {/* PDF DOWNLOAD ONLY */}
                                                        <td className="text-center align-middle">
                                                            <button
                                                                type="button"
                                                                title="Download Voucher PDF"
                                                                aria-label="Download Voucher PDF"
                                                                disabled={
                                                                    submitting ||
                                                                    Boolean(pdfDownloadingId)
                                                                }
                                                                onClick={() =>
                                                                    handleDownloadVoucher(voucherId)
                                                                }
                                                                style={{
                                                                    display: "inline-flex",
                                                                    alignItems: "center",
                                                                    justifyContent: "center",
                                                                    background: "transparent",
                                                                    border: "none",
                                                                    boxShadow: "none",
                                                                    padding: "6px 10px",
                                                                    color: "#dc3545",
                                                                    cursor:
                                                                        submitting || pdfDownloadingId
                                                                            ? "not-allowed"
                                                                            : "pointer"
                                                                }}
                                                            >
                                                                {pdfDownloadingId === voucherId ? (
                                                                    <span
                                                                        className="spinner-border spinner-border-sm"
                                                                        role="status"
                                                                        aria-label="Generating PDF"
                                                                    />
                                                                ) : (
                                                                    <svg
                                                                        xmlns="http://www.w3.org/2000/svg"
                                                                        width="27"
                                                                        height="27"
                                                                        viewBox="0 0 24 24"
                                                                        fill="none"
                                                                        stroke="#dc3545"
                                                                        strokeWidth="1.8"
                                                                        strokeLinecap="round"
                                                                        strokeLinejoin="round"
                                                                        aria-hidden="true"
                                                                        style={{
                                                                            display: "block",
                                                                            flexShrink: 0
                                                                        }}
                                                                    >
                                                                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                                                        <polyline points="14 2 14 8 20 8" />
                                                                        <path d="M8 13h2a1.5 1.5 0 0 1 0 3H8v-3z" />
                                                                        <path d="M8 16v2" />
                                                                        <path d="M13 13v5" />
                                                                        <path d="M13 13h1a2 2 0 0 1 0 4h-1" />
                                                                        <path d="M18 13h-2v5" />
                                                                        <path d="M16 15.5h1.5" />
                                                                    </svg>
                                                                )}
                                                            </button>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    )}


                    <div className="card border-0 shadow-sm mb-3">
                        <div className="card-header bg-white d-flex justify-content-between align-items-center">
                            <strong>
                                Eligible Approved Cash Out Entries
                            </strong>

                            <span className="badge bg-secondary">
                                {selectedEntryIds.length} Selected
                            </span>
                        </div>

                        <div className="card-body p-0">
                            <div className="table-responsive">
                                <table className="table table-hover table-bordered align-middle mb-0">
                                    <thead className="table-light">
                                        <tr>
                                            <th style={{ width: 45 }}>
                                                <input
                                                    type="checkbox"
                                                    checked={allSelected}
                                                    disabled={
                                                        !entries.length || submitting
                                                    }
                                                    onChange={toggleAll}
                                                />
                                            </th>
                                            <th>Entry No.</th>
                                            <th>Date</th>
                                            <th>Employee</th>
                                            <th>Category</th>
                                            <th>Vendor</th>
                                            <th>Remark</th>
                                            <th className="text-end">
                                                Amount
                                            </th>
                                        </tr>
                                    </thead>

                                    <tbody>
                                        {entries.length === 0 ? (
                                            <tr>
                                                <td
                                                    colSpan={8}
                                                    className="text-center text-muted py-4"
                                                >
                                                    No eligible approved Cash Out
                                                    entries found.
                                                </td>
                                            </tr>
                                        ) : (
                                            entries.map((entry) => {
                                                const id = getId(entry);

                                                return (
                                                    <tr key={id}>
                                                        <td>
                                                            <input
                                                                type="checkbox"
                                                                checked={selectedEntryIds.includes(
                                                                    id
                                                                )}
                                                                disabled={submitting}
                                                                onChange={() =>
                                                                    toggleEntry(id)
                                                                }
                                                            />
                                                        </td>

                                                        <td>
                                                            {entry.requestNumber || "-"}
                                                        </td>

                                                        <td>
                                                            {new Date(
                                                                entry.dateTime
                                                            ).toLocaleDateString(
                                                                "en-IN",
                                                                {
                                                                    timeZone:
                                                                        "Asia/Kolkata"
                                                                }
                                                            )}
                                                        </td>

                                                        <td>
                                                            {getName(entry.employee)}
                                                        </td>

                                                        <td>
                                                            {getName(entry.category)}
                                                        </td>

                                                        <td>
                                                            {getName(entry.vendor)}
                                                        </td>

                                                        <td>
                                                            {entry.remark || "-"}
                                                        </td>

                                                        <td className="text-end">
                                                            {money(entry.amount)}
                                                        </td>
                                                    </tr>
                                                );
                                            })
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>

                    {entries.length > 0 && (
                        <div className="card border-0 shadow-sm">
                            <div className="card-body">
                                <div className="row g-3">
                                    <div className="col-12">
                                        <label className="form-label fw-bold">
                                            Voucher Action
                                        </label>

                                        <div className="d-flex flex-wrap gap-3">
                                            <label className="form-check-label">
                                                <input
                                                    type="radio"
                                                    className="form-check-input me-2"
                                                    name="voucherMode"
                                                    checked={voucherMode === "new"}
                                                    disabled={submitting}
                                                    onChange={() => {
                                                        setVoucherMode("new");
                                                        setSelectedVoucher(null);
                                                    }}
                                                />
                                                Generate New Voucher
                                            </label>

                                            {existingVouchers.length > 0 && (
                                                <label className="form-check-label">
                                                    <input
                                                        type="radio"
                                                        className="form-check-input me-2"
                                                        name="voucherMode"
                                                        checked={
                                                            voucherMode === "merge"
                                                        }
                                                        disabled={submitting}
                                                        onChange={() =>
                                                            setVoucherMode("merge")
                                                        }
                                                    />
                                                    Merge Into Existing Voucher
                                                </label>
                                            )}
                                        </div>
                                    </div>

                                    {voucherMode === "merge" && (
                                        <div className="col-md-6">
                                            <label className="form-label">
                                                Select Existing Voucher
                                            </label>

                                            <Select
                                                options={voucherOptions}
                                                value={selectedVoucher}
                                                onChange={setSelectedVoucher}
                                                isDisabled={submitting}
                                                placeholder="Select voucher to merge"
                                            />
                                        </div>
                                    )}

                                    <div className="col-12">
                                        <div className="d-flex flex-wrap justify-content-between align-items-center gap-3">
                                            <div>
                                                <small className="text-muted">
                                                    Selected Amount
                                                </small>

                                                <h4 className="mb-0">
                                                    {money(selectedTotal)}
                                                </h4>
                                            </div>

                                            <button
                                                type="button"
                                                className="btn btn-success"
                                                disabled={
                                                    submitting ||
                                                    selectedEntryIds.length === 0 ||
                                                    (voucherMode === "merge" &&
                                                        !selectedVoucher)
                                                }
                                                onClick={handleSubmit}
                                            >
                                                <i className="fa fa-check me-2" />

                                                {submitting
                                                    ? "Processing..."
                                                    : voucherMode === "merge"
                                                        ? "Merge Selected Entries"
                                                        : "Generate New Voucher"}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </>
            )}

            {lastVoucher && (
                <div className="alert alert-success mt-3 d-flex flex-wrap align-items-center justify-content-between gap-2">
                    <div>
                        <strong>
                            Voucher {lastVoucher.voucherNumber}
                        </strong>
                        {" — "}
                        Revision R{lastVoucher.revision || 0}
                        {" — "}
                        {money(lastVoucher.totalAmount)}
                    </div>

                    <div className="d-flex align-items-center gap-2">
                        {/* VIEW GENERATED VOUCHER */}
                        <button
                            type="button"
                            className="btn btn-sm btn-outline-success"
                            onClick={() =>
                                handleViewVoucher(getId(lastVoucher))
                            }
                        >
                            <i className="fa fa-eye me-1" />
                            View Voucher
                        </button>

                        {/* DOWNLOAD GENERATED VOUCHER */}
                        <button
                            type="button"
                            className="btn btn-sm btn-success"
                            disabled={Boolean(pdfDownloadingId)}
                            onClick={() =>
                                handleDownloadVoucher(getId(lastVoucher))
                            }
                        >
                            <i className="fa fa-file-pdf-o me-2" />

                            {pdfDownloadingId === getId(lastVoucher)
                                ? "Generating..."
                                : "Download PDF"}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CashVoucherGenerator;
